import React, { useRef, useState, useEffect } from 'react'
import axios from 'axios'

import Panel from '../ui/Panel'
import Btn from '../ui/Btn'
import FileUploadSponsor from './FileUploadSponsor'
import FileUploadLogoSponsor from './FileUploadLogoSponsor'
import SponsorLang from './SponsorLang'
import SponsorVideos from './SponsorVideos'
import { Notice, Confirm, Field, SkeletonForm, IconTrash } from './media'
import './media.css'

/*
 * SPONSOR — the form.
 *
 * Requests, payloads and response handling are untouched:
 *   GET    admin/sponsors/{id}
 *   GET    admin/sports
 *   POST   admin/sponsors        (the whole record object, as before)
 *   DELETE admin/sponsors/{id}
 *
 * The screen is now a stack of panels — identity, images, languages, videos —
 * in the order the record is actually built. alert() and window.confirm are
 * gone: the same messages appear inline, in the panel that produced them.
 */

/* The API answers dates as either "2024-05-01" or a full ISO stamp; <input
   type="date"> only accepts the first form and silently blanks the second.
   Display-side only — an untouched field still posts exactly what it loaded. */
const dateValue = (value) => {
  const raw = value == null ? '' : String(value)
  return /^\d{4}-\d{2}-\d{2}/.test(raw) ? raw.slice(0, 10) : raw
}

export default function SponsorForm(props) {

  const [record, setRecord] = useState({})
  const [isLoading, setIsLoading] = useState(false)
  const [descr, setDescr] = useState('')
  const [sports, setSports] = useState([])
  const [note, setNote] = useState(null)
  const [confirmDel, setConfirmDel] = useState(false)
  const [saving, setSaving] = useState(false)

  const timerRef = useRef(null)

  useEffect(() => {
    loadData(props.id)
    loadSports()
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [props.id])

  useEffect(() => () => { if (timerRef.current) window.clearTimeout(timerRef.current) }, [])

  const loadData = (id) => {
    setIsLoading(true)
    if (id != 0) {
      axios.get(process.env.REACT_APP_API_SERVER + 'admin/sponsors/' + id, {
        headers: {
          'Authorization': 'Bearer ' + sessionStorage.getItem("apiToken")
        }
      })
        .then(res => {
          setRecord(res.data.results)
          setDescr(res.data.results.Descr)

          setIsLoading(false)

        }).catch(err => {
          setIsLoading(false)
          setNote({ kind: 'crit', text: 'The sponsor could not be loaded.' })
        })
    } else {
      setRecord({
        id: 0,
        IsActive: 1,
        Descr: '',
        IsChallenge: 0
      })
      setIsLoading(false)
    }
  }

  const loadSports = () => {
    setIsLoading(true)
    axios.get(process.env.REACT_APP_API_SERVER + 'admin/sports', {
      headers: {
        'Authorization': 'Bearer ' + sessionStorage.getItem("apiToken")
      }
    })
      .then(res => {
        setSports(res.data.results)
        setIsLoading(false)

      }).catch(err => {
        setIsLoading(false)
        setNote({ kind: 'crit', text: 'The sports could not be loaded.' })
      })
  }

  const save = (e) => {
    e.preventDefault()

    const obj = { ...record }
    setSaving(true)
    setNote(null)

    axios.post(process.env.REACT_APP_API_SERVER + 'admin/sponsors', obj, {
      headers: {
        'Authorization': 'Bearer ' + sessionStorage.getItem("apiToken")
      }
    })
      .then(res => {
        setSaving(false)
        setNote({ kind: 'good', text: res.data.msg || 'Saved.' })
        /* The message is shown where the action happened, then the screen goes
           back to the list exactly as it did before. */
        timerRef.current = window.setTimeout(() => { props.showForm(0, false) }, 900)
      }).catch(err => {
        setSaving(false)
        setNote({ kind: 'crit', text: 'Something went wrong, please try again' })
      })
  }

  const deleteRecord = () => {
    setConfirmDel(false)
    axios.delete(process.env.REACT_APP_API_SERVER + 'admin/sponsors/' + props.id, {
      headers: {
        'Authorization': 'Bearer ' + sessionStorage.getItem("apiToken")
      }
    })
      .then(res => {
        if (res.data.err == 0) {
          props.loadData()
          props.showForm(0, false)
        } else {
          setNote({ kind: 'crit', text: res.data.msg || 'Something went wrong, please try again' })
        }

      }).catch(err => {
        setNote({ kind: 'crit', text: 'Something went wrong, please try again' })
      })
  }

  const sportsList = sports.map(item => {
    return (
      <option key={item.SportID} value={item.SportID}>{item.SportName}</option>
    )
  })

  const isNew = props.id == 0

  return (
    <div className="md-stack">
      <Panel
        title={isNew ? 'New Sponsor' : 'Sponsor Details'}
        subtitle={isNew ? 'Fill in the basic details and save' : record.Title || ''}
        actions={<Btn onClick={() => props.showForm(0, false)}>Sponsors List</Btn>}
      >
        {note ? (
          <Notice kind={note.kind} onClose={() => setNote(null)}>{note.text}</Notice>
        ) : null}

        {isLoading && !isNew && !record.Title ? (
          <SkeletonForm rows={4} />
        ) : (
          <form className="md-form" onSubmit={save}>
            <div className="md-group">
              <Field label="Sport" htmlFor="sp-sport" required>
                <select
                  id="sp-sport"
                  className="md-select"
                  required
                  value={record.SportID == null ? '' : record.SportID}
                  onChange={(e) => setRecord({ ...record, SportID: e.target.value })}
                >
                  <option value=''>[Select sport]</option>
                  {sportsList}
                </select>
              </Field>

              <Field label={(record.IsChallenge == 1 ? 'Challenge' : 'Article') + ' Title'} htmlFor="sp-title" required>
                <input
                  id="sp-title"
                  className="md-input"
                  type="text"
                  required
                  value={record.Title == null ? '' : record.Title}
                  onChange={(e) => setRecord({ ...record, Title: e.target.value })}
                />
              </Field>

              <Field label="Description" htmlFor="sp-descr" className="md-span">
                <textarea
                  id="sp-descr"
                  className="md-textarea"
                  rows={3}
                  value={record.Descr == null ? '' : record.Descr}
                  onChange={(e) => setRecord({ ...record, Descr: e.target.value })}
                />
              </Field>

              <Field label="From" htmlFor="sp-from" required>
                <input
                  id="sp-from"
                  className="md-input"
                  type="date"
                  required
                  value={dateValue(record.DateFrom)}
                  onChange={(e) => setRecord({ ...record, DateFrom: e.target.value })}
                />
              </Field>

              <Field label="To" htmlFor="sp-to" required>
                <input
                  id="sp-to"
                  className="md-input"
                  type="date"
                  required
                  value={dateValue(record.DateTo)}
                  onChange={(e) => setRecord({ ...record, DateTo: e.target.value })}
                />
              </Field>

              <Field label="Active" htmlFor="sp-active">
                <select
                  id="sp-active"
                  className="md-select"
                  value={record.IsActive == null ? '1' : record.IsActive}
                  onChange={(e) => setRecord({ ...record, IsActive: e.target.value })}
                >
                  <option value='1'>YES</option>
                  <option value='0'>NO</option>
                </select>
              </Field>
            </div>

            {confirmDel ? (
              <Confirm
                text={<b>DELETE THE ENTIRE SPONSOR!!!! ARE YOU SURE???</b>}
                confirmLabel="Delete"
                cancelLabel="Cancel"
                onConfirm={deleteRecord}
                onCancel={() => setConfirmDel(false)}
              />
            ) : null}

            <div className="md-actions md-actions-split">
              {!isNew ? (
                <Btn className="md-danger md-actions-left" onClick={() => setConfirmDel(true)}>
                  {IconTrash}
                  Delete
                </Btn>
              ) : <span className="md-actions-left" />}

              <Btn variant="accent" type="submit" disabled={saving}>
                {saving ? 'Saving...' : 'Save'}
              </Btn>
            </div>
          </form>
        )}
      </Panel>

      {!isNew ? (
        <>
          <FileUploadSponsor id={props.id} type="1" Photo={record.Photo} loadData={loadData} record={record} />
          <FileUploadLogoSponsor id={props.id} type="1" Photo={record.Photo} loadData={loadData} record={record} />
          <SponsorLang id={props.id} IsChallenge={record.IsChallenge} />
          <SponsorVideos id={props.id} IsChallenge={record.IsChallenge} />
        </>
      ) : null}
    </div>
  )
}
